import './style.modules.scss'

import {
  Button,
  ButtonModifiers,
  ButtonVariant,
  MaterialIcon
} from '@ampcore/adel-react'
import {
  AmpcoreMaterialIconId,
  BackgroundColor
} from '@ampcore/ampcom-contracts'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import xss from 'xss'

import { Spacer, SpacerSpacing } from '../../main'
import { AccoladeBackgroundColor } from './AccoladeBackground.type'
import { AccoladesBackgroundColor } from './AccoladesBackground.type'

export type Accolade = {
  image: string
  description: string
  background?: AccoladeBackgroundColor
  hasBorder?: boolean
}

export type AccoladesProps = {
  componentSpacing?: SpacerSpacing
  componentBackground?: AccoladesBackgroundColor
  accolades: Accolade[]
  variant?: 'stacked' | 'carousel'
  header?: string
  testId?: string
}

export const Accolades = (props: AccoladesProps) => {
  const {
    componentSpacing,
    componentBackground,
    accolades,
    header,
    variant,
    testId
  } = props

  const [position, setPosition] = useState(0)
  const [positionOffset, setPositionOffset] = useState(0)
  const [accoladeWidth, setAccoladeWidth] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  const [startX, setStartX] = useState(0)
  const [startY, setStartY] = useState(0)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const GAP = 20

  const moveLeft = useCallback(
    (jumpAmount: number) => {
      if (position === 0) return
      if (isNaN(jumpAmount) || jumpAmount === 0) {
        setPosition(position - 1)
        return
      }
      if (position - jumpAmount <= 0) {
        setPosition(0)
        return
      }
      setPosition(position - jumpAmount)
    },
    [position]
  )

  const moveRight = useCallback(
    (jumpAmount: number) => {
      if (!accolades || position === accolades.length - 1) return
      if (isNaN(jumpAmount) || jumpAmount === 0) {
        setPosition(position + 1)
        return
      }
      if (position + jumpAmount >= accolades.length) {
        setPosition(accolades.length - 1)
        return
      }
      setPosition(position + jumpAmount)
    },
    [position, accolades]
  )

  const onSwipe = useCallback(
    (deltaX: number, deltaY: number) => {
      if (Math.abs(deltaY) > 100) return
      const numAccoladesMoved = deltaX / (accoladeWidth + GAP)
      if (deltaX > 50) moveLeft(Math.floor(numAccoladesMoved))
      if (deltaX < -50) moveRight(Math.ceil(Math.abs(numAccoladesMoved)))
    },
    [moveLeft, moveRight, accoladeWidth]
  )

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!wrapperRef.current?.contains(e.target as HTMLElement)) return
    e.preventDefault()
    setIsSwiping(true)
    setStartX(e.touches[0].clientX)
    setStartY(e.touches[0].clientY)
  }, [])

  const handleTouch = useCallback(
    (e: TouchEvent) => {
      if (!wrapperRef.current?.contains(e.target as HTMLElement)) return
      e.preventDefault()
      setPositionOffset(e.changedTouches[0].clientX - startX)
    },
    [startX]
  )

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!wrapperRef.current?.contains(e.target as HTMLElement)) return
      e.preventDefault()
      const endX = e.changedTouches[0].clientX
      const endY = e.changedTouches[0].clientY
      onSwipe(endX - startX, endY - startY)
      setPositionOffset(0)
      setIsSwiping(false)
    },
    [startX, startY, onSwipe]
  )

  useEffect(() => {
    if (variant !== 'carousel') return
    window.addEventListener('touchstart', handleTouchStart)
    window.addEventListener('touchmove', handleTouch)
    window.addEventListener('touchend', handleTouchEnd)
    return () => {
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchmove', handleTouch)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [variant, handleTouchStart, handleTouch, handleTouchEnd])

  useEffect(() => {
    if (variant !== 'carousel') return
    const handleWindowSizeChange = () => {
      if (!wrapperRef.current) return
      const size = wrapperRef.current.getBoundingClientRect()
      if (size.width + 60 < 768) {
        setAccoladeWidth(size.width - 20)
        return
      }
      if (size.width + 60 < 1240) {
        setAccoladeWidth((size.width - 60) / 3)
        return
      }
      setAccoladeWidth((size.width - 340) / 3)
    }
    handleWindowSizeChange()
    window.addEventListener('resize', handleWindowSizeChange)
    return () => window.removeEventListener('resize', handleWindowSizeChange)
  }, [variant])

  if (!accolades || accolades.length === 0) {
    return null
  }

  function getAccoladesBackgroundColor(
    background?: AccoladesBackgroundColor | AccoladeBackgroundColor
  ) {
    switch (background) {
      case BackgroundColor.STONE:
        return 'u-bgColorStone'
      case BackgroundColor.PURPLE_TINT:
        return 'u-bgColorPurpleLight10'
      case BackgroundColor.LIME_TINT:
        return 'u-bgColorLimeLight20'
      case BackgroundColor.MINT_TINT:
        return 'u-bgColorMintLight10'
      case BackgroundColor.BERRY_TINT:
        return 'u-bgColorBerryLight10'
      case BackgroundColor.ORANGE_TINT:
        return 'u-bgColorOrangeLight20'
      case BackgroundColor.TRANSPARENT:
        return ''
      default:
        return 'u-bgColorWhite'
    }
  }

  function getAccoladesBackgroundFacet(
    background?: AccoladesBackgroundColor | AccoladeBackgroundColor
  ) {
    switch (background) {
      case BackgroundColor.WHITE_FACET:
        return `url(${window.AMPCOMPATH}images/facets/accolades/white.png)`
      case BackgroundColor.MIDNIGHT_BLUE_FACET:
        return `url(${window.AMPCOMPATH}images/facets/accolades/midnight-blue.png)`
      case BackgroundColor.PURPLE_FACET:
        return `url(${window.AMPCOMPATH}images/facets/accolades/purple.png)`
      case BackgroundColor.LIGHT_BLUE_FACET:
        return `url(${window.AMPCOMPATH}images/facets/accolades/light-blue.png)`
      case BackgroundColor.DARK_MINT_FACET:
        return `url(${window.AMPCOMPATH}images/facets/accolades/dark-mint.png)`
      case BackgroundColor.MINT_FACET:
        return `url(${window.AMPCOMPATH}images/facets/accolades/mint.png)`
      case BackgroundColor.BERRY_FACET:
        return `url(${window.AMPCOMPATH}images/facets/accolades/berry.png)`
      case BackgroundColor.ORANGE_FACET:
        return `url(${window.AMPCOMPATH}images/facets/accolades/orange.png)`
      default:
        return ''
    }
  }

  let facetColor = ''
  if (componentBackground == BackgroundColor.WHITE_FACET) {
    facetColor = 'white'
  } else if (componentBackground == BackgroundColor.MIDNIGHT_BLUE_FACET) {
    facetColor = 'midnight-blue'
  }

  const isContentReversed: boolean =
    componentBackground === BackgroundColor.MIDNIGHT_BLUE_FACET

  const layoutClasses: string[] = []
  switch (accolades.length) {
    case 2:
      layoutClasses.push('u-md-size1of2 u-lg-size1of2')
      break
    case 3:
      layoutClasses.push('u-md-size1of3 u-lg-size1of3')
      break
    case 4:
      layoutClasses.push('u-md-size1of2 u-lg-size1of4')
      break
    default:
      layoutClasses.push('u-md-size1of3 u-lg-size1of3')
      break
  }

  const hasRemainderRow = accolades.length > 4 && accolades.length % 3 !== 0
  const fullRowsCount = hasRemainderRow
    ? Math.floor(accolades.length / 3) * 3
    : accolades.length
  const firstRowAccolades = hasRemainderRow
    ? accolades.slice(0, fullRowsCount)
    : accolades
  const remainderAccolades = hasRemainderRow
    ? accolades.slice(fullRowsCount)
    : []

  const renderAccolade = (
    accolade: Accolade,
    index: number,
    accoladeClass?: string
  ) => (
    <div
      className={`u-paddingBottom24${accoladeClass ? ` ${accoladeClass}` : ''}`}
      key={index}
      data-testid={testId ? `${testId}-accolade-${index}` : undefined}
    >
      <div
        className={`Accolade u-padding24 u-flex u-flexCol u-flexJustifyItemsCenter u-flexAlignItemsCenter ${getAccoladesBackgroundColor(
          accolade.background
        )} ${accolade.hasBorder ? 'u-border' : ''}`}
        style={{ background: getAccoladesBackgroundFacet(accolade.background) }}
      >
        <div className="Accolade-image u-flexShrink0 u-overflowHidden u-flexJustifyCenter u-flexAlignItemsCenter">
          <img
            src={`${accolade.image}`}
            alt=""
            data-testid={
              testId ? `${testId}-accolade-${index}-image` : undefined
            }
          />
        </div>
        <div
          className={`Accolade-content u-paddingTop24 u-sizeFull ${
            (accolade.background === BackgroundColor.TRANSPARENT &&
              componentBackground === BackgroundColor.MIDNIGHT_BLUE_FACET) ||
            (accolade.background?.endsWith('facet') &&
              accolade.background !== BackgroundColor.WHITE_FACET)
              ? 'u-textColorWhite'
              : 'u-textColorNeutralGrey'
          }`}
          dangerouslySetInnerHTML={{
            __html: xss(accolade.description)
          }}
          data-testid={
            testId ? `${testId}-accolade-${index}-description` : undefined
          }
        ></div>
      </div>
    </div>
  )

  if (variant === 'carousel') {
    return (
      <Spacer spacing={componentSpacing}>
        <section
          className={`Accolades u-paddingTop24 ${getAccoladesBackgroundColor(
            componentBackground
          )}`}
          style={{
            ...(facetColor && {
              backgroundImage: `url(${window.AMPCOMPATH}images/facets/banner/${facetColor}.svg)`
            })
          }}
          data-testid={testId}
        >
          {header && (
            <div
              className={`Accolades-header u-paddingBottom16 u-sizeConstrained Type-4 ${
                isContentReversed
                  ? 'u-textColorWhite'
                  : 'u-textColorNeutralGrey'
              }`}
              dangerouslySetInnerHTML={{ __html: xss(header) }}
            ></div>
          )}
          <div
            className={`Accolades-withCarousel u-paddingLeft32 u-paddingRight40${
              position !== 0
                ? ' Accolades-shifted u-paddingLeft40 u-sm-paddingLeft40 u-md-paddingLeft40'
                : ''
            } u-sizeFull u-sm-paddingRight40 u-md-paddingRight40 u-marginBottom4`}
          >
            <div
              className={`Accolades-overflowWrapper u-posRelative u-marginBottom20${
                isSwiping ? ' Accolades-overflowWrapper--noTransition' : ''
              }`}
              style={{
                gridTemplateColumns: `repeat(${accolades.length}, ${accoladeWidth}px)`,
                right: position * (accoladeWidth + GAP) - positionOffset
              }}
              ref={wrapperRef}
              data-testid={testId ? `${testId}-wrapper` : null}
            >
              {accolades.map((accolade, index) =>
                renderAccolade(accolade, index)
              )}
            </div>
          </div>
          <div className="Accolades-carouselControls u-paddingBottom16 u-flex u-flexAlignItemsEnd u-sizeConstrained u-marginBottom4 u-posRelative">
            <Button
              variant={ButtonVariant.SECONDARY}
              modifiers={isContentReversed ? [ButtonModifiers.REVERSED] : []}
              className={`Button--icon Button--icon--stroke u-flex u-padding0`}
              onClick={() => moveLeft(0)}
              disabled={position === 0}
              testId={testId ? `${testId}-slide-left` : undefined}
            >
              <MaterialIcon
                icon={AmpcoreMaterialIconId.CHEVRON_LEFT}
                ariaLabel="Slide Left"
              />
            </Button>
            <Button
              variant={ButtonVariant.SECONDARY}
              modifiers={isContentReversed ? [ButtonModifiers.REVERSED] : []}
              className={`Button--icon Button--icon--stroke u-flex u-padding0`}
              onClick={() => moveRight(0)}
              disabled={position === accolades.length - 1}
              testId={testId ? `${testId}-slide-right` : undefined}
            >
              <MaterialIcon
                icon={AmpcoreMaterialIconId.CHEVRON_RIGHT}
                ariaLabel="Slide Right"
              />
            </Button>
            <p
              className={`${
                isContentReversed ? 'u-textColorWhite' : 'u-textColorActionBlue'
              }`}
            >
              {position + 1} / {accolades.length}
            </p>
          </div>
        </section>
      </Spacer>
    )
  }

  return (
    <Spacer spacing={componentSpacing}>
      <section
        className={`Accolades u-paddingTop24 ${getAccoladesBackgroundColor(
          componentBackground
        )}`}
        style={{
          ...(facetColor && {
            backgroundImage: `url(${window.AMPCOMPATH}images/facets/banner/${facetColor}.svg)`
          })
        }}
        data-testid={testId}
      >
        {header && (
          <div
            className={`Accolades-header u-paddingBottom16 u-sizeConstrained Type-4 ${
              isContentReversed ? 'u-textColorWhite' : 'u-textColorNeutralGrey'
            }`}
            dangerouslySetInnerHTML={{ __html: xss(header) }}
          ></div>
        )}
        <div className="Grid Grid--withGutter u-sizeConstrained">
          {firstRowAccolades.map((accolade, index) =>
            renderAccolade(accolade, index, layoutClasses.join(' '))
          )}
        </div>
        {remainderAccolades.length > 0 && (
          <div className="Grid Grid--withGutter u-sizeConstrained u-flexJustifyCenter">
            {remainderAccolades.map((accolade, index) =>
              renderAccolade(accolade, fullRowsCount + index, 'u-size1of2')
            )}
          </div>
        )}
      </section>
    </Spacer>
  )
}
